import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Share2, Link2, Loader2, Plus, Trash2, Pencil, Instagram, MessageCircle,
  Users, Check, X, TrendingUp, AlertCircle, Search, FlaskConical, RefreshCw,
  Facebook, Copy,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../context/AuthContext';

// ---------------------------------------------------------------------------
// Meta Direct lane — the ONLY lane the mobile + web clients drive. The
// previous third-party middleware was fully retired on Feb 28, 2026.
// ---------------------------------------------------------------------------
const CHANNELS = [
  { value: 'INSTAGRAM', label: 'Instagram', Icon: Instagram },
  { value: 'FACEBOOK_MESSENGER', label: 'Messenger', Icon: MessageCircle },
];
export const CHANNEL_META = Object.fromEntries(CHANNELS.map((c) => [c.value, c]));

function describeError(e) {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.message || detail.error || JSON.stringify(detail);
  return e?.message || 'Something went wrong';
}

function fmtRelative(iso) {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Meta Direct connect card
// ---------------------------------------------------------------------------
function SubscriptionChip({ label, status, Icon }) {
  // status is { ok: bool, code?, message? } | null | undefined
  const ok = status?.ok === true;
  const explicitlyBad = status && status.ok === false;
  const tone = ok
    ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
    : explicitlyBad
      ? 'bg-red-100 text-red-800 border-red-200'
      : 'bg-brand-cream text-brand-green/70 border-brand-green/15';
  return (
    <span
      data-testid={`meta-sub-chip-${label.toLowerCase()}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide ${tone}`}
      title={status?.message || (ok ? 'subscribed' : 'not subscribed')}
    >
      <Icon className="w-3 h-3" /> {label} {ok ? '✓' : explicitlyBad ? '✕' : '…'}
    </span>
  );
}

function MetaConnectCard({ binding, lanDisabled, onBindingChange }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const bound = binding?.bound === true;

  const connect = () => {
    // Browser-side nav (not XHR) — Meta's OAuth dialog requires a top-level
    // document. The server resolver reads our httpOnly auth cookie, so a
    // plain navigation suffices from the web CRM.
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const qs = new URLSearchParams({
      return_to: '/crm/settings/social-media',
    });
    window.location.href = `${base}/api/meta/oauth/start?${qs.toString()}`;
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect your Facebook Page + Instagram? Your keyword campaigns stay and will re-activate the moment you reconnect.')) return;
    setBusy(true);
    try {
      await api.post('/meta/disconnect');
      toast({ title: 'Disconnected' });
      onBindingChange({ bound: false });
    } catch (e) {
      toast({ title: 'Disconnect failed', description: describeError(e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const retry = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/meta/subscriptions/retry');
      toast({ title: 'Subscriptions refreshed' });
      onBindingChange({ ...binding, subscription_status: data.subscription_status, subscription_updated_at: data.subscription_updated_at });
    } catch (e) {
      toast({ title: 'Retry failed', description: describeError(e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  if (lanDisabled) {
    return (
      <Card className="p-6 space-y-3" data-testid="meta-connect-card-disabled">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-1" />
          <div>
            <h2 className="font-serif text-xl text-brand-green">Social Media Auto-DM is being enabled</h2>
            <p className="text-sm text-brand-green/70 mt-1">
              We&rsquo;re finishing the Meta App Review handoff. The Connect button appears here the moment Meta
              approves our scopes — no action needed on your side.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  if (!bound) {
    return (
      <Card className="p-6 space-y-4" data-testid="meta-connect-card">
        <div className="flex items-start gap-3">
          <Share2 className="w-5 h-5 text-brand-gold mt-1" />
          <div>
            <h2 className="font-serif text-xl text-brand-green">Connect Facebook Page + Instagram</h2>
            <p className="text-sm text-brand-green/70 mt-1">
              Auto-DM prospective buyers with property info and your AI voice number when they
              use a keyword in an Instagram comment, Instagram DM, or Facebook Messenger thread.
              Native integration — direct Meta Business Login, no third-party middleware.
            </p>
          </div>
        </div>
        <div>
          <Button
            onClick={connect}
            disabled={busy}
            data-testid="meta-connect-submit"
            className="bg-brand-green text-brand-cream hover:bg-brand-green/90"
          >
            <Facebook className="w-4 h-4 mr-2" />
            Connect with Facebook
          </Button>
        </div>
        <p className="text-[11px] text-brand-green/50">
          You&rsquo;ll be redirected to Facebook Business Login. Pick the Page + Instagram account that
          belongs to this listing hub.
        </p>
      </Card>
    );
  }

  const subs = binding?.subscription_status || {};

  return (
    <Card className="p-6 space-y-4" data-testid="meta-connect-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
            <Check className="w-4 h-4 text-emerald-700" />
          </div>
          <div>
            <div className="font-serif text-lg text-brand-green">
              Connected to {binding.page_name || binding.page_id || 'Facebook Page'}
            </div>
            <div className="text-xs text-brand-green/60 mt-0.5 flex items-center gap-2 flex-wrap">
              <span>Bound {fmtRelative(binding.bound_at)}</span>
              {binding.ig_username && (
                <span className="inline-flex items-center gap-1">
                  <Instagram className="w-3 h-3" /> @{binding.ig_username}
                </span>
              )}
              {binding.ig_pending && !binding.ig_username && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200"
                  data-testid="meta-ig-pending-badge"
                  title="Meta hasn't yet granted the permission needed to read your Instagram link. Messenger auto-DMs work now; reconnect once Meta approves the review to unlock Instagram."
                >
                  <Instagram className="w-3 h-3" /> Instagram pending Meta review
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={retry} disabled={busy}
            data-testid="meta-subscriptions-retry"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${busy ? 'animate-spin' : ''}`} />
            Retry subscriptions
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={disconnect} disabled={busy}
            data-testid="meta-disconnect"
            className="text-red-700 hover:bg-red-50"
          >
            <X className="w-3 h-3 mr-1" /> Disconnect
          </Button>
        </div>
      </div>
      <div className="rounded-lg bg-brand-cream p-3 flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wide text-brand-green/60 flex items-center gap-1">
          <Link2 className="w-3 h-3" /> Webhook subscriptions
        </span>
        <SubscriptionChip label="Page" status={subs.page} Icon={Facebook} />
        <SubscriptionChip label="Instagram" status={subs.instagram} Icon={Instagram} />
        {binding.subscription_updated_at && (
          <span className="text-[11px] text-brand-green/50 ml-auto">
            Checked {fmtRelative(binding.subscription_updated_at)}
          </span>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Add / edit campaign dialog — Oct 2026: brought to full parity with the
// mobile app. Schema-driven, no hardcoded channel/trigger matrices.
// ---------------------------------------------------------------------------

// Response-mode preset templates (mirror the mobile app's 5 quick-starts).
// All templates are editable after tap-to-fill.
const RESPONSE_PRESETS = [
  {
    id: 'open_house',
    label: 'Open House',
    text: "Hi {first_name}! Thanks for your interest in {listing_address} 🏡 We're hosting an open house soon — reply here and I'll send you the date and time. Listed at {price}, {beds} bed / {baths} bath.",
  },
  {
    id: 'price_drop',
    label: 'Price Drop',
    text: 'Hi {first_name}! Great news — the price on {listing_address} just dropped to {price}. {beds} bed / {baths} bath, {sqft} sqft. Want the full details or a private showing?',
  },
  {
    id: 'just_listed',
    label: 'Just Listed',
    text: "Hi {first_name}! {listing_address} just hit the market — {price}, {beds} bed / {baths} bath, {sqft} sqft. Reply here and I'll send photos and help you set up a tour. 📸",
  },
  {
    id: 'schedule_tour',
    label: 'Schedule a Tour',
    text: 'Hi {first_name}! Happy to show you {listing_address} in person. What day works best? You can also call {voice_number} and my assistant will book it. {price}, {beds} bed / {baths} bath.',
  },
  {
    id: 'more_info',
    label: 'More Photos / Info',
    text: "Hi {first_name}! Here's more on {listing_address} — {price}, {beds} bed / {baths} bath, {sqft} sqft. Want extra photos, the floor plan, or a tour? Just reply and I'll send everything over.",
  },
];

// Human-readable trigger source labels (shown in UI; machine value stays
// uppercase in payloads). Short descriptions mirror the mobile app copy.
const TRIGGER_SOURCE_META = {
  POST_COMMENT: {
    label: 'Comment',
    hint: 'Fires when someone comments on a post',
  },
  DIRECT_DM: {
    label: 'Direct message',
    hint: 'Fires when the keyword is sent to the inbox',
  },
  STORY_REPLY: {
    label: 'Story reply',
    hint: 'Fires when someone replies to a story (Instagram only)',
  },
};

// Pre-filled the first time a realtor flips the public-reply switch on,
// mirrors COMMENT_REPLY_DEFAULT_TEMPLATE in social_media_routes.py.
const COMMENT_REPLY_DEFAULT_TEMPLATE = 'Hey, {first_name}! Just sent you a DM.';

// Fallback schema used ONLY if the live /schema fetch fails. Keeps the
// modal usable in a flaky-network scenario — same shape the backend
// returns today. If the backend ships new trigger sources this will go
// stale; the live fetch is the source of truth.
const SCHEMA_FALLBACK = {
  channels: ['INSTAGRAM', 'FACEBOOK_MESSENGER'],
  channel_source_matrix: {
    INSTAGRAM: ['POST_COMMENT', 'DIRECT_DM', 'STORY_REPLY'],
    FACEBOOK_MESSENGER: ['DIRECT_DM', 'POST_COMMENT'],
  },
  template_variables: [
    { token: '{first_name}', description: "Sender's first name" },
    { token: '{listing_address}', description: 'Street address of the listing' },
    { token: '{price}', description: 'Formatted list price' },
    { token: '{beds}', description: 'Bedroom count' },
    { token: '{baths}', description: 'Bathroom count' },
    { token: '{sqft}', description: 'Living area in sqft' },
    { token: '{voice_number}', description: 'AI voice assistant phone number' },
    { token: '{keyword}', description: 'The keyword that triggered this campaign' },
  ],
  comment_reply_template_variables: [
    { token: '{first_name}', description: "Commenter's first name" },
    { token: '{page_name}', description: 'Your Facebook Page name' },
  ],
  comment_reply_template_max_chars: 500,
  limits: {
    keyword_min_chars: 2,
    keyword_max_chars: 40,
    keywords_per_campaign_max: 20,
    trigger_sources_per_campaign_max: 3,
    response_template_max_chars: 1000,
    name_max_chars: 120,
  },
};

const EMPTY_FORM = {
  listing_id: '',
  name: '',
  keywords: [],
  channel: 'INSTAGRAM',
  trigger_sources: ['DIRECT_DM'],
  external_post_url: '',
  external_post_id: '',
  response_mode: 'AI',
  response_template: '',
  fire_on_any_comment: false,
  comment_reply_enabled: false,
  comment_reply_template: '',
  is_active: true,
};

export function CampaignDialog({ open, listings, editing, onClose, onSaved, initialListingId = '' }) {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [schema, setSchema] = useState(SCHEMA_FALLBACK);
  const [kwInput, setKwInput] = useState('');
  const replyRef = React.useRef(null);
  const templateRef = React.useRef(null);
  const kwInputRef = React.useRef(null);

  // Fetch schema once per dialog open — short-circuit on close so we
  // don't thrash the backend when the editor rapidly opens/closes.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/social-media/schema');
        if (!cancelled && data) setSchema({ ...SCHEMA_FALLBACK, ...data });
      } catch {
        /* keep fallback */
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (editing) {
      // Backend sends BOTH legacy singular `keyword` + the new `keywords`
      // array. Prefer the array when present, fall back to legacy so
      // older campaigns pre-array still open correctly.
      const kws = Array.isArray(editing.keywords) && editing.keywords.length
        ? editing.keywords
        : (editing.keyword ? [editing.keyword] : []);
      const srcs = Array.isArray(editing.trigger_sources) && editing.trigger_sources.length
        ? editing.trigger_sources
        : (editing.trigger_source ? [editing.trigger_source]
          : (editing.trigger_type === 'SPECIFIC_POST'
              ? ['POST_COMMENT'] : ['DIRECT_DM']));
      setForm({
        listing_id: editing.listing_id,
        name: editing.name || '',
        keywords: kws.map((k) => k.toUpperCase()),
        channel: editing.channel,
        trigger_sources: srcs,
        external_post_url: editing.external_post_url || '',
        external_post_id: editing.external_post_id || '',
        response_mode: editing.response_mode || 'AI',
        response_template: editing.response_template || '',
        fire_on_any_comment: !!editing.fire_on_any_comment,
        comment_reply_enabled: !!editing.comment_reply_enabled,
        comment_reply_template: editing.comment_reply_template || '',
        is_active: !!editing.is_active,
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        // Prefer explicit `initialListingId` (set when the editor is
        // opened from a specific listing's "Add keyword" button) so the
        // campaign binds to that listing without the realtor re-picking it.
        listing_id: initialListingId || listings[0]?.id || '',
      });
    }
    setKwInput('');
  }, [editing, listings, open, initialListingId]);

  const limits = schema.limits || SCHEMA_FALLBACK.limits;
  const kwLimits = {
    min: limits.keyword_min_chars ?? 2,
    max: limits.keyword_max_chars ?? 40,
    count: limits.keywords_per_campaign_max ?? 20,
  };
  const maxSources = Math.max(
    1,
    schema.trigger_sources_per_campaign_max
      || limits.trigger_sources_per_campaign_max
      || 3,
  );
  const nameMax = limits.name_max_chars ?? 120;
  const templateMax = limits.response_template_max_chars ?? 1000;
  const replyMax = schema.comment_reply_template_max_chars ?? 500;
  const templateVars = schema.template_variables || SCHEMA_FALLBACK.template_variables;
  const commentReplyVars =
    schema.comment_reply_template_variables || SCHEMA_FALLBACK.comment_reply_template_variables;

  // Channel compatibility — only trigger sources permitted by the live
  // matrix for the picked channel are selectable. Switching channel
  // auto-drops now-invalid sources and keeps at least one.
  const allowedSources = (
    schema.channel_source_matrix?.[form.channel]
    || SCHEMA_FALLBACK.channel_source_matrix[form.channel]
    || []
  );
  const hasPostComment = form.trigger_sources.includes('POST_COMMENT');
  const isFbPostComment = form.channel === 'FACEBOOK_MESSENGER' && hasPostComment;
  const hasPostId = !!form.external_post_id.trim();

  // ---- Field helpers --------------------------------------------- //
  const addKeyword = useCallback(() => {
    const raw = kwInput.trim().toUpperCase();
    if (raw.length < kwLimits.min) return;
    if (raw.length > kwLimits.max) return;
    if (form.keywords.includes(raw)) {
      setKwInput('');
      return;
    }
    if (form.keywords.length >= kwLimits.count) {
      toast({
        title: `Up to ${kwLimits.count} keywords`, variant: 'destructive',
      });
      return;
    }
    setForm((f) => ({ ...f, keywords: [...f.keywords, raw] }));
    setKwInput('');
    requestAnimationFrame(() => kwInputRef.current?.focus());
  }, [kwInput, form.keywords, kwLimits, toast]);

  const removeKeyword = (kw) =>
    setForm((f) => ({ ...f, keywords: f.keywords.filter((k) => k !== kw) }));

  const onChannelChange = (ch) => {
    const nextMatrix = schema.channel_source_matrix?.[ch]
      || SCHEMA_FALLBACK.channel_source_matrix[ch] || [];
    let nextSources = form.trigger_sources.filter((s) => nextMatrix.includes(s));
    if (!nextSources.length) nextSources = nextMatrix.slice(0, 1);
    setForm((f) => ({ ...f, channel: ch, trigger_sources: nextSources }));
  };

  const toggleSource = (src) => {
    const has = form.trigger_sources.includes(src);
    if (has) {
      if (form.trigger_sources.length === 1) return;  // need ≥1
      setForm((f) => ({ ...f, trigger_sources: f.trigger_sources.filter((s) => s !== src) }));
      return;
    }
    if (form.trigger_sources.length >= maxSources) {
      toast({ title: `Up to ${maxSources} trigger sources`, variant: 'destructive' });
      return;
    }
    setForm((f) => ({ ...f, trigger_sources: [...f.trigger_sources, src] }));
  };

  const insertTokenInto = (ref, field, token) => {
    const el = ref.current;
    const cur = form[field] || '';
    if (!el) {
      setForm((f) => ({ ...f, [field]: cur + token }));
      return;
    }
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + token + cur.slice(end);
    setForm((f) => ({ ...f, [field]: next }));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const applyPreset = (preset) => {
    setForm((f) => ({ ...f, response_template: preset.text }));
    requestAnimationFrame(() => templateRef.current?.focus());
  };

  // ---- Submit ---------------------------------------------------- //
  const submit = async () => {
    if (!form.listing_id) return toast({ title: 'Pick a listing', variant: 'destructive' });
    if (!form.keywords.length) {
      return toast({ title: 'Add at least one keyword', variant: 'destructive' });
    }
    if (!form.trigger_sources.length) {
      return toast({ title: 'Pick at least one trigger source', variant: 'destructive' });
    }
    if (hasPostComment && !form.external_post_id.trim()
          && !form.external_post_url.trim()) {
      // Not strictly required by backend — SPECIFIC_POST is derived from
      // whether external_post_id is set — but we warn so realtors don't
      // leave an empty-id campaign thinking it's post-scoped.
    }
    if (form.response_mode === 'TEMPLATE' && !form.response_template.trim()) {
      return toast({
        title: 'Add your DM template or switch to AI-written',
        variant: 'destructive',
      });
    }
    if (form.comment_reply_enabled && !form.comment_reply_template.trim()) {
      return toast({
        title: 'Add a reply message or turn off the public reply',
        variant: 'destructive',
      });
    }

    setBusy(true);
    try {
      // Payload matches the full mobile-app contract. Legacy singular
      // fields (keyword, trigger_source) are also sent so older
      // backend code paths and third-party integrations keep working.
      const body = {
        listing_id: form.listing_id,
        name: form.name.trim() || null,
        keywords: form.keywords,
        keyword: form.keywords[0],
        channel: form.channel,
        trigger_sources: form.trigger_sources,
        trigger_source: form.trigger_sources[0],
        external_post_url: hasPostComment ? form.external_post_url.trim() || null : null,
        external_post_id: hasPostComment ? form.external_post_id.trim() || null : null,
        response_mode: form.response_mode,
        response_template: form.response_mode === 'TEMPLATE'
          ? form.response_template.trim() : null,
        fire_on_any_comment: !!(isFbPostComment && hasPostId && form.fire_on_any_comment),
        comment_reply_enabled: !!(isFbPostComment && form.comment_reply_enabled),
        comment_reply_template: form.comment_reply_enabled
          ? (form.comment_reply_template.trim() || null) : null,
        is_active: form.is_active,
      };
      const url = `/listings/${form.listing_id}/social-campaign`
                  + (editing ? `?campaign_id=${editing.id}` : '');
      const { data } = await api.post(url, body);
      toast({ title: editing ? 'Campaign updated' : 'Campaign added' });
      onSaved(data);
      onClose();
    } catch (e) {
      toast({ title: 'Save failed', description: describeError(e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  // ---- Render ---------------------------------------------------- //
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="social-campaign-dialog"
                     className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit keyword campaign' : 'Add keyword campaign'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Listing */}
          <div>
            <Label htmlFor="camp-listing">Listing</Label>
            <Select value={form.listing_id}
                    onValueChange={(v) => setForm({ ...form, listing_id: v })}>
              <SelectTrigger id="camp-listing" data-testid="social-campaign-listing">
                <SelectValue placeholder="Pick a listing" />
              </SelectTrigger>
              <SelectContent>
                {listings.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.address || l.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Campaign name (optional) */}
          <div>
            <Label htmlFor="camp-name">
              Campaign name <span className="text-xs text-brand-green/60">(optional)</span>
            </Label>
            <Input id="camp-name" data-testid="social-campaign-name"
                   value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Maple Grove open house"
                   maxLength={nameMax} />
          </div>

          {/* Keywords (chip input) */}
          <div>
            <Label htmlFor="camp-kw-input">
              Keywords{' '}
              <span className="text-xs text-brand-green/60">
                ({form.keywords.length}/{kwLimits.count})
              </span>
            </Label>
            <div className="flex gap-2 mt-1">
              <Input id="camp-kw-input" ref={kwInputRef}
                     data-testid="social-campaign-keyword-input"
                     value={kwInput}
                     onChange={(e) => setKwInput(e.target.value.toUpperCase())}
                     onKeyDown={(e) => {
                       if (e.key === 'Enter' || e.key === ',') {
                         e.preventDefault();
                         addKeyword();
                       } else if (e.key === 'Backspace' && !kwInput && form.keywords.length) {
                         removeKeyword(form.keywords[form.keywords.length - 1]);
                       }
                     }}
                     placeholder="HAZEL"
                     maxLength={kwLimits.max} />
              <Button type="button" variant="outline"
                      data-testid="social-campaign-keyword-add"
                      onClick={addKeyword}
                      disabled={kwInput.trim().length < kwLimits.min}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {form.keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2"
                   data-testid="social-campaign-keyword-chips">
                {form.keywords.map((kw) => (
                  <span key={kw}
                        className="inline-flex items-center gap-1 rounded-full border border-brand-green/30 bg-brand-cream/50 px-2 py-0.5 text-xs font-mono text-brand-green">
                    {kw}
                    <button type="button"
                            data-testid={`social-campaign-keyword-remove-${kw}`}
                            onClick={() => removeKeyword(kw)}
                            className="text-brand-green/60 hover:text-brand-green">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-[11px] text-brand-green/60 mt-1">
              Buyers who send <em>any</em> of these words trigger the campaign. {kwLimits.min}–{kwLimits.max} chars.
            </p>
          </div>

          {/* Channel */}
          <div>
            <Label htmlFor="camp-ch">Channel</Label>
            <Select value={form.channel} onValueChange={onChannelChange}>
              <SelectTrigger id="camp-ch" data-testid="social-campaign-channel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(schema.channels || SCHEMA_FALLBACK.channels).map((c) => {
                  const meta = CHANNEL_META[c] || { label: c };
                  return <SelectItem key={c} value={c}>{meta.label}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Trigger sources (multi-select) */}
          <div>
            <Label>
              Trigger sources{' '}
              <span className="text-xs text-brand-green/60">
                ({form.trigger_sources.length}/{maxSources})
              </span>
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
              {['POST_COMMENT', 'DIRECT_DM', 'STORY_REPLY'].map((src) => {
                const meta = TRIGGER_SOURCE_META[src];
                const allowed = allowedSources.includes(src);
                const selected = form.trigger_sources.includes(src);
                return (
                  <button key={src} type="button"
                          data-testid={`social-campaign-trigger-${src}`}
                          onClick={() => allowed && toggleSource(src)}
                          disabled={!allowed}
                          className={`rounded-lg border p-3 text-left text-sm transition-colors ${
                            !allowed ? 'opacity-40 cursor-not-allowed border-brand-green/10'
                              : selected ? 'border-brand-green bg-brand-green/5'
                                : 'border-brand-green/20 hover:bg-brand-cream/50'
                          }`}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-brand-green">{meta.label}</span>
                      {selected && <Check className="w-3.5 h-3.5 text-brand-green" />}
                    </div>
                    <div className="text-[11px] text-brand-green/60 mt-0.5">{meta.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Post URL/ID — only when POST_COMMENT is a source */}
          {hasPostComment && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="camp-url">
                  Post URL <span className="text-xs text-brand-green/60">(optional)</span>
                </Label>
                <Input id="camp-url" data-testid="social-campaign-post-url"
                       value={form.external_post_url}
                       onChange={(e) => setForm({ ...form, external_post_url: e.target.value })}
                       placeholder="https://instagram.com/p/…" />
              </div>
              <div>
                <Label htmlFor="camp-pid">
                  Post / asset ID <span className="text-xs text-brand-green/60">(for one-post scope)</span>
                </Label>
                <Input id="camp-pid" data-testid="social-campaign-post-id"
                       value={form.external_post_id}
                       onChange={(e) => setForm({ ...form, external_post_id: e.target.value })}
                       placeholder="ig_mediaid_1234" />
              </div>
            </div>
          )}

          {/* Facebook POST_COMMENT bypass */}
          {isFbPostComment && (
            <div data-testid="social-campaign-fb-any-comment-row"
                 className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label htmlFor="camp-any-comment" className="text-sm font-medium text-amber-900">
                    Reply to <em>any</em> comment on this post
                  </Label>
                  <div className="text-[11px] text-amber-900/70 mt-0.5">
                    Skips keyword matching and fires on every new comment under the specific post ID above.
                    Use this while Meta reviews <span className="font-mono">pages_read_user_content</span>.
                  </div>
                </div>
                <Switch id="camp-any-comment"
                        data-testid="social-campaign-any-comment"
                        checked={form.fire_on_any_comment && hasPostId}
                        disabled={!hasPostId}
                        onCheckedChange={(v) => setForm({ ...form, fire_on_any_comment: v })} />
              </div>
              {!hasPostId && (
                <div className="text-[11px] text-amber-900/80">
                  Add a Post / asset ID above to enable this bypass.
                </div>
              )}
            </div>
          )}

          {/* Facebook POST_COMMENT public reply */}
          {isFbPostComment && (
            <div data-testid="social-campaign-comment-reply-row"
                 className="rounded-lg border border-brand-green/20 bg-brand-cream/40 p-3 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label htmlFor="camp-comment-reply" className="text-sm font-medium text-brand-green">
                    Also post a public reply to the triggering comment
                  </Label>
                  <div className="text-[11px] text-brand-green/70 mt-0.5">
                    After the DM sends successfully, drop a public reply under the commenter.
                    Requires <span className="font-mono">pages_manage_engagement</span> on the Page token.
                  </div>
                </div>
                <Switch id="camp-comment-reply"
                        data-testid="social-campaign-comment-reply-toggle"
                        checked={form.comment_reply_enabled}
                        onCheckedChange={(v) => setForm({
                          ...form,
                          comment_reply_enabled: v,
                          comment_reply_template: (v && !form.comment_reply_template.trim())
                            ? COMMENT_REPLY_DEFAULT_TEMPLATE
                            : form.comment_reply_template,
                        })} />
              </div>
              {form.comment_reply_enabled && (
                <div className="space-y-2">
                  <Textarea
                    ref={replyRef}
                    data-testid="social-campaign-comment-reply-template"
                    value={form.comment_reply_template}
                    onChange={(e) => setForm({ ...form, comment_reply_template: e.target.value })}
                    placeholder="Hey {first_name}! Just sent you a DM — {page_name} 🏡"
                    maxLength={replyMax} rows={3} />
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] text-brand-green/60 mr-1">Insert:</span>
                    {commentReplyVars.map((v) => (
                      <button key={v.token} type="button"
                              data-testid={`social-campaign-comment-reply-var-${v.token.replace(/[{}]/g, '')}`}
                              onClick={() => insertTokenInto(replyRef, 'comment_reply_template', v.token)}
                              title={v.description}
                              className="rounded-full border border-brand-green/30 bg-white px-2 py-0.5 text-[11px] font-mono text-brand-green hover:bg-brand-green/5">
                        {v.token}
                      </button>
                    ))}
                    <span className="ml-auto text-[11px] text-brand-green/50">
                      {form.comment_reply_template.length}/{replyMax}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Response mode */}
          <div>
            <Label>Reply mode</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[
                { v: 'AI', title: 'AI-written',
                  hint: 'The backend writes a friendly DM with property details.' },
                { v: 'TEMPLATE', title: 'Template',
                  hint: 'You write the exact DM. 8 auto-fill tokens available.' },
              ].map((opt) => (
                <button key={opt.v} type="button"
                        data-testid={`social-campaign-mode-${opt.v}`}
                        onClick={() => setForm({ ...form, response_mode: opt.v })}
                        className={`rounded-lg border p-3 text-left text-sm transition-colors ${
                          form.response_mode === opt.v
                            ? 'border-brand-green bg-brand-green/5'
                            : 'border-brand-green/20 hover:bg-brand-cream/50'
                        }`}>
                  <div className="font-medium text-brand-green">{opt.title}</div>
                  <div className="text-[11px] text-brand-green/60 mt-0.5">{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Template editor */}
          {form.response_mode === 'TEMPLATE' && (
            <div data-testid="social-campaign-template-block"
                 className="rounded-lg border border-brand-green/20 bg-white p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">DM template</Label>
                <span className="text-[11px] text-brand-green/50">
                  {form.response_template.length}/{templateMax}
                </span>
              </div>
              {/* Quick-start presets */}
              <div className="flex flex-wrap gap-1.5">
                <span className="text-[11px] text-brand-green/60 mr-1">Quick-start:</span>
                {RESPONSE_PRESETS.map((p) => (
                  <button key={p.id} type="button"
                          data-testid={`social-campaign-preset-${p.id}`}
                          onClick={() => applyPreset(p)}
                          className="rounded-full border border-brand-green/30 bg-brand-cream/40 px-2.5 py-0.5 text-[11px] text-brand-green hover:bg-brand-green/5">
                    {p.label}
                  </button>
                ))}
              </div>
              <Textarea ref={templateRef}
                        data-testid="social-campaign-template"
                        value={form.response_template}
                        onChange={(e) => setForm({ ...form, response_template: e.target.value })}
                        placeholder="Hi {first_name}! Thanks for your interest in {listing_address}…"
                        maxLength={templateMax} rows={5} />
              {/* Variable chips */}
              <div className="flex flex-wrap gap-1.5">
                <span className="text-[11px] text-brand-green/60 mr-1">Insert:</span>
                {templateVars.map((v) => (
                  <button key={v.token} type="button"
                          data-testid={`social-campaign-template-var-${v.token.replace(/[{}]/g, '')}`}
                          onClick={() => insertTokenInto(templateRef, 'response_template', v.token)}
                          title={v.description}
                          className="rounded-full border border-brand-green/30 bg-white px-2 py-0.5 text-[11px] font-mono text-brand-green hover:bg-brand-green/5">
                    {v.token}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Active */}
          <div className="flex items-center justify-between pt-1">
            <Label htmlFor="camp-active">Active</Label>
            <Switch id="camp-active" data-testid="social-campaign-active"
                    checked={form.is_active}
                    onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="social-campaign-save"
                  className="bg-brand-green text-brand-cream hover:bg-brand-green/90">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : (editing ? 'Save changes' : 'Add campaign')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Campaigns list
// ---------------------------------------------------------------------------
function CampaignRow({ campaign, onEdit, onDelete, onToggleActive, onTest }) {
  const meta = CHANNEL_META[campaign.channel] || CHANNELS[0];
  const Icon = meta.Icon;
  const specific = campaign.trigger_type === 'SPECIFIC_POST';
  // Oct 2026: list display mirrors the mobile app — show the user-set
  // name when present, otherwise join the keyword list. The legacy
  // `keyword` singular stays as the mono tag chip underneath so
  // long-time users can still spot campaigns by their keyword fast.
  const kws = Array.isArray(campaign.keywords) && campaign.keywords.length
    ? campaign.keywords
    : (campaign.keyword ? [campaign.keyword] : []);
  const title = (campaign.name || '').trim() || kws.join(' · ') || '—';
  return (
    <div data-testid={`social-campaign-row-${campaign.id}`}
         className="rounded-lg border border-brand-green/10 bg-white p-3 flex flex-col md:flex-row md:items-center gap-3">
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center flex-wrap gap-2">
          <span className="font-serif font-semibold text-brand-green text-base">
            {title}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-brand-cream text-brand-green px-2 py-0.5 text-[10px] uppercase tracking-wide border border-brand-green/10">
            <Icon className="w-3 h-3" /> {meta.label}
          </span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${
            specific ? 'bg-yellow-100 text-yellow-900' : 'bg-emerald-100 text-emerald-800'
          }`}>
            {specific ? 'Specific post' : 'Global catch-all'}
          </span>
          {!campaign.is_active && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-[10px] uppercase tracking-wide">
              Paused
            </span>
          )}
        </div>
        {kws.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-0.5">
            {kws.slice(0, 6).map((kw) => (
              <span key={kw}
                    className="font-mono text-[11px] text-brand-green/80 bg-brand-cream/60 border border-brand-green/10 rounded px-1.5 py-0.5">
                {kw}
              </span>
            ))}
            {kws.length > 6 && (
              <span className="text-[11px] text-brand-green/60">+{kws.length - 6}</span>
            )}
          </div>
        )}
        <div className="text-[11px] text-brand-green/60 flex items-center gap-3 flex-wrap"
             data-testid={`social-campaign-analytics-${campaign.id}`}>
          <span className="inline-flex items-center gap-1">
            <TrendingUp className="w-3 h-3 text-brand-gold" />
            <b className="text-brand-green">{campaign.trigger_count || 0}</b>&nbsp;
            trigger{campaign.trigger_count === 1 ? '' : 's'}
          </span>
          <span>·</span>
          <span>Last fired {fmtRelative(campaign.last_triggered_at)}</span>
          {campaign.external_post_url && (
            <>
              <span>·</span>
              <a href={campaign.external_post_url} target="_blank" rel="noreferrer"
                 className="underline">Post</a>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Switch checked={campaign.is_active}
                data-testid={`social-campaign-toggle-${campaign.id}`}
                onCheckedChange={(v) => onToggleActive(campaign, v)} />
        <Button variant="ghost" size="sm" onClick={() => onTest(campaign)}
                data-testid={`social-campaign-test-${campaign.id}`}
                title="Preview the AI reply — no DM is sent">
          <FlaskConical className="w-3 h-3 mr-1" /> Test
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onEdit(campaign)}
                data-testid={`social-campaign-edit-${campaign.id}`}>
          <Pencil className="w-3 h-3 mr-1" /> Edit
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(campaign)}
                data-testid={`social-campaign-delete-${campaign.id}`}
                className="text-red-700 hover:bg-red-50">
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Test preview dialog
// ---------------------------------------------------------------------------
function TestPreviewDialog({ state, onClose }) {
  const { toast } = useToast();
  if (!state) return null;
  const { campaign, loading, data, error } = state;
  const channelLabel = (CHANNEL_META[campaign?.channel] || CHANNELS[0]).label;

  const copyReply = () => {
    if (!data?.property_summary) return;
    navigator.clipboard.writeText(data.property_summary);
    toast({ title: 'Copied' });
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="social-test-preview-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Preview — <span className="font-mono">{campaign?.keyword}</span>
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-brand-green/70 -mt-2">
          This is exactly what the AI will DM buyers who use this keyword on <b>{channelLabel}</b>.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-brand-green/70 py-6"
               data-testid="social-test-preview-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Rendering the AI reply…
          </div>
        )}

        {error && !loading && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2"
               data-testid="social-test-preview-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {data && !loading && (
          <div className="space-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-brand-green/60 mb-1">
                Property snapshot
              </div>
              <pre data-testid="social-test-preview-summary"
                   className="whitespace-pre-wrap font-sans text-sm text-brand-green bg-brand-cream/60 rounded p-3 border border-brand-green/10">
{data.property_summary || '—'}
              </pre>
            </div>
            {data.property_link && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-brand-green/60 mb-1">
                  Link
                </div>
                <a href={data.property_link} target="_blank" rel="noopener noreferrer"
                   data-testid="social-test-preview-link"
                   className="text-sm text-brand-green underline break-all">
                  {data.property_link}
                </a>
              </div>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-wide text-brand-green/60 mb-1">
                AI voice number
              </div>
              {data.ai_voice_number ? (
                <a href={`tel:${data.ai_voice_number}`}
                   data-testid="social-test-preview-voice"
                   className="text-sm text-brand-green underline">
                  {data.ai_voice_number}
                </a>
              ) : (
                <span className="text-xs text-brand-green/60"
                      data-testid="social-test-preview-voice-empty">
                  No AI voice number assigned yet — set one under Settings → AI Voice.
                </span>
              )}
            </div>
          </div>
        )}

        <p className="text-[11px] text-brand-green/50">
          Preview only — no real DM is sent, and this does not count as a trigger.
        </p>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="social-test-preview-close">
            Close
          </Button>
          <Button onClick={copyReply}
                  disabled={!data?.property_summary}
                  data-testid="social-test-preview-copy"
                  className="bg-brand-green text-brand-cream hover:bg-brand-green/90">
            <Copy className="w-3 h-3 mr-1" /> Copy reply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignsSection({ bound, listings, campaigns, onCampaignsChanged }) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState('by_listing');
  const [query, setQuery] = useState('');
  const [testing, setTesting] = useState(null);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const c of campaigns) {
      const key = c.listing_id;
      if (!map.has(key)) map.set(key, { address: c.listing_address, items: [] });
      map.get(key).items.push(c);
    }
    return [...map.entries()];
  }, [campaigns]);

  const flatSorted = useMemo(() => {
    const arr = [...campaigns];
    arr.sort((a, b) => {
      const ta = a.last_triggered_at ? new Date(a.last_triggered_at).getTime() : null;
      const tb = b.last_triggered_at ? new Date(b.last_triggered_at).getTime() : null;
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return tb - ta;
    });
    return arr;
  }, [campaigns]);

  const flatFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return flatSorted;
    return flatSorted.filter((c) =>
      (c.keyword || '').toLowerCase().includes(q) ||
      (c.listing_address || '').toLowerCase().includes(q),
    );
  }, [flatSorted, query]);

  const totals = useMemo(() => campaigns.reduce(
    (acc, c) => ({
      triggers: acc.triggers + (c.trigger_count || 0),
      active: acc.active + (c.is_active ? 1 : 0),
    }), { triggers: 0, active: 0 },
  ), [campaigns]);

  const toggle = async (c, v) => {
    try {
      await api.post(`/listings/${c.listing_id}/social-campaign?campaign_id=${c.id}`,
        { ...c, is_active: v });
      onCampaignsChanged();
    } catch (e) {
      toast({ title: 'Toggle failed', description: describeError(e), variant: 'destructive' });
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete keyword "${c.keyword}"?`)) return;
    try {
      await api.delete(`/listings/${c.listing_id}/social-campaign/${c.id}`);
      onCampaignsChanged();
      toast({ title: `Deleted ${c.keyword}` });
    } catch (e) {
      toast({ title: 'Delete failed', description: describeError(e), variant: 'destructive' });
    }
  };

  const runTest = async (c) => {
    setTesting({ campaign: c, loading: true, data: null, error: null });
    try {
      const { data } = await api.post(`/social-media/campaigns/${c.id}/test-preview`);
      setTesting({ campaign: c, loading: false, data, error: null });
    } catch (e) {
      setTesting({ campaign: c, loading: false, data: null, error: describeError(e) });
    }
  };

  if (!bound) return null;

  return (
    <Card className="p-6 space-y-4" data-testid="social-campaigns-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-serif text-lg text-brand-green">Keyword campaigns</div>
          <div className="text-xs text-brand-green/60 mt-1">
            <b className="text-brand-green">{totals.active}</b> active ·
            <b className="text-brand-green"> {totals.triggers}</b> lifetime triggers
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-md border border-brand-green/15 bg-white p-0.5"
               data-testid="social-campaigns-view-toggle">
            <button type="button"
                    onClick={() => setView('by_listing')}
                    data-testid="social-campaigns-view-by-listing"
                    className={`px-2.5 py-1 text-xs rounded ${
                      view === 'by_listing'
                        ? 'bg-brand-green text-brand-cream'
                        : 'text-brand-green/70 hover:bg-brand-cream'
                    }`}>
              By listing
            </button>
            <button type="button"
                    onClick={() => setView('all')}
                    data-testid="social-campaigns-view-all"
                    className={`px-2.5 py-1 text-xs rounded ${
                      view === 'all'
                        ? 'bg-brand-green text-brand-cream'
                        : 'text-brand-green/70 hover:bg-brand-cream'
                    }`}>
              All keywords
            </button>
          </div>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }}
                  disabled={listings.length === 0}
                  data-testid="social-campaign-add"
                  className="bg-brand-gold text-brand-green hover:bg-brand-gold/90">
            <Plus className="w-4 h-4 mr-1" /> Add keyword
          </Button>
        </div>
      </div>
      <div
        className="rounded-md border border-brand-green/15 bg-brand-cream/40 px-3 py-2 text-xs text-brand-green/80 leading-relaxed"
        data-testid="social-campaigns-helper"
      >
        Pick a keyword for this listing, then mention it in your post caption
        (e.g. <span className="font-mono">"Comment SKIROS for full details!"</span>).
        When someone comments or DMs that keyword, they instantly get the
        property details below. Works across all your posts — no need to
        link a specific one.
      </div>
      {listings.length === 0 && (
        <div className="text-xs text-brand-green/60">
          Create a listing first so you can bind a keyword to it.
        </div>
      )}
      {campaigns.length === 0 && listings.length > 0 && (
        <div className="text-xs text-brand-green/60" data-testid="social-campaigns-empty">
          No keyword campaigns yet. Click <b>Add keyword</b> to link your first Instagram or Messenger keyword to a listing.
        </div>
      )}
      {view === 'by_listing' && grouped.map(([listingId, group]) => (
        <div key={listingId} className="space-y-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-brand-green/60">
            <Users className="w-3 h-3" />
            <span className="truncate">{group.address || 'Untitled listing'}</span>
            <span className="text-brand-green/40">· {group.items.length}</span>
          </div>
          <div className="space-y-2">
            {group.items.map((c) => (
              <CampaignRow key={c.id} campaign={c}
                           onEdit={(x) => { setEditing(x); setDialogOpen(true); }}
                           onDelete={remove}
                           onToggleActive={toggle}
                           onTest={runTest} />
            ))}
          </div>
        </div>
      ))}
      {view === 'all' && campaigns.length > 0 && (
        <>
          <div className="relative" data-testid="social-campaigns-search-wrap">
            <Search className="w-4 h-4 text-brand-green/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search keyword or listing…"
              data-testid="social-campaigns-search"
              className="pl-9 pr-9"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                data-testid="social-campaigns-search-clear"
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-brand-cream"
              >
                <X className="w-3.5 h-3.5 text-brand-green/60" />
              </button>
            )}
          </div>
          {flatFiltered.length === 0 ? (
            <div className="text-xs text-brand-green/60 flex items-center gap-2"
                 data-testid="social-campaigns-search-empty">
              No campaigns match &ldquo;{query}&rdquo;.
              <Button variant="ghost" size="sm" onClick={() => setQuery('')}
                      data-testid="social-campaigns-search-empty-clear">
                Clear
              </Button>
            </div>
          ) : (
            <div className="space-y-2" data-testid="social-campaigns-all-list">
              {flatFiltered.map((c) => (
                <div key={c.id} className="space-y-1">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-brand-green/50">
                    <Users className="w-3 h-3" />
                    <span className="truncate">{c.listing_address || 'Untitled listing'}</span>
                  </div>
                  <CampaignRow campaign={c}
                               onEdit={(x) => { setEditing(x); setDialogOpen(true); }}
                               onDelete={remove}
                               onToggleActive={toggle}
                               onTest={runTest} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <CampaignDialog
        open={dialogOpen} listings={listings} editing={editing}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSaved={() => onCampaignsChanged()}
      />
      <TestPreviewDialog
        state={testing}
        onClose={() => setTesting(null)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function CrmSettingsSocialMedia() {
  const { toast } = useToast();
  const [binding, setBinding] = useState(null);
  // null = not yet probed, true = /meta/binding returned 404 (lane off)
  const [lanDisabled, setLanDisabled] = useState(null);
  const [listings, setListings] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const reloadCampaigns = useCallback(async () => {
    try {
      const { data } = await api.get('/social-media/campaigns');
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (e) { /* toast handled by caller */ }
  }, []);

  const loadAll = useCallback(async () => {
    try {
      // Feature-detect the Meta Direct lane. A 404 means the backend
      // flag is OFF — mirror the mobile app's behaviour by hiding the
      // connect flow behind a "being enabled" state.
      let bindingData = null;
      let laneOff = false;
      try {
        const { data } = await api.get('/meta/binding');
        bindingData = data;
      } catch (e) {
        if (e?.response?.status === 404) {
          laneOff = true;
        } else {
          throw e;
        }
      }

      const [listRes, campsRes] = await Promise.all([
        api.get('/listings'),
        api.get('/social-media/campaigns').catch(() => ({ data: [] })),
      ]);
      setBinding(bindingData);
      setLanDisabled(laneOff);
      setListings(Array.isArray(listRes.data) ? listRes.data : []);
      setCampaigns(Array.isArray(campsRes.data) ? campsRes.data : []);

      // Surface a toast if the OAuth round-trip just landed back here.
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const meta = params.get('meta');
        if (meta === 'connected') {
          const page = params.get('page');
          const igPending = params.get('ig_pending') === '1';
          if (igPending) {
            toast({
              title: `Page connected${page ? ` — ${page}` : ''}`,
              description: 'Messenger auto-DMs are live. Instagram linking is pending Meta\'s feature review — you can reconnect once approved to attach Instagram.',
            });
          } else {
            toast({ title: `Connected${page ? ` to ${page}` : ''}` });
          }
          window.history.replaceState({}, '', window.location.pathname);
        } else if (meta === 'denied') {
          toast({ title: 'Connect cancelled', description: 'Facebook login was dismissed.', variant: 'destructive' });
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
    } catch (e) {
      toast({ title: 'Failed to load Social Media', description: describeError(e), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="space-y-6" data-testid="crm-social-media">
      <div>
        <h2 className="font-serif text-2xl text-brand-green">Social Media</h2>
        <p className="text-sm text-brand-green/70 mt-1">
          Auto-DM buyers on Instagram or Facebook Messenger when they use a keyword — reply with
          property info and your AI voice number. Native Meta integration — direct Business Login.
        </p>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-brand-green/70 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <MetaConnectCard
            binding={binding}
            lanDisabled={!!lanDisabled}
            onBindingChange={setBinding}
          />
          <CampaignsSection
            bound={binding?.bound === true}
            listings={listings}
            campaigns={campaigns}
            onCampaignsChanged={reloadCampaigns}
          />
        </>
      )}
    </div>
  );
}
